import React from 'react';

import { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS, Node } from '@contentful/rich-text-types';
import { ImageLoaderProps } from 'next/image';
import sanitizeHtml from 'sanitize-html';

import { ACCEPTED_VIDEO_MIME_TYPES } from '../../../constants/constants';
import { isBrowser } from '../../../helpers/environment/isBrowser';
import { reportMykissError } from '../../../helpers/errorHandling';
import { getContentfulLoader } from '../../../helpers/imageLoaders/contentful';
import { ExternalLink } from '../../Clickables/ExternalLink/ExternalLink';
import { InternalLink } from '../../Clickables/InternalLink/InternalLink';
import { Divider } from '../../Divider/Divider';
import { Heading } from '../../Heading/Heading';
import { NextImageWrapper } from '../../NextImage/NextImageWrapper';
import { Text } from '../../Text/Text';

export const getRichTextOptions = (): Options => ({
  renderNode: {
    [BLOCKS.HR]: (_: Node) => <Divider variant="md" />,
    [BLOCKS.LIST_ITEM]: (_: Node, children: React.ReactNode) => (
      <li className="-mt-2">{children}</li> // NB: over writing styling of child <p
    ),
    [BLOCKS.OL_LIST]: (_: Node, children: React.ReactNode) => (
      <ol className="list-decimal">{children}</ol>
    ),
    [BLOCKS.UL_LIST]: (_: Node, children: React.ReactNode) => (
      <ul className="list-disc">{children}</ul>
    ),
    [BLOCKS.PARAGRAPH]: (_: Node, children: React.ReactNode) => (
      <Text className="mb-0">{children}</Text>
    ),
    [BLOCKS.QUOTE]: (_: Node, children: React.ReactNode) => (
      <blockquote className="italic">{children}</blockquote>
    ),
    [BLOCKS.HEADING_1]: (_: Node, children: React.ReactNode) => (
      <Heading as="h1" variant="2xl">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_2]: (_: Node, children: React.ReactNode) => (
      <Heading as="h2" variant="sm" className="mb-4">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_3]: (_: Node, children: React.ReactNode) => (
      <Heading as="h3" variant="xs" className="mb-4">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_4]: (_: Node, children: React.ReactNode) => (
      <Heading as="h4" variant="xs" className="mb-4">
        {children}
      </Heading>
    ),
    [BLOCKS.EMBEDDED_ENTRY]: (node: Node) => {
      const { image, url } = node.data.target.fields;
      if (!image || !url) {
        return <></>;
      }
      const { width: origWidth, height: origHeight } = image.fields.file.details.image;
      const contentfulLoader = ({ src, width, quality }: ImageLoaderProps): string =>
        getContentfulLoader(src, width, origWidth, origHeight, {
          quality,
        });
      const parsedUrl = new URL(url);

      if (parsedUrl.hostname === 'www.fishbrain.com') {
        return (
          <InternalLink to={parsedUrl.pathname}>
            <NextImageWrapper
              imageClassName="relative my-0 h-auto max-w-full rounded-lg"
              customPlaceholderClassName="rounded-lg"
              alt={image.fields.title || 'blogpost image'}
              src={image.fields.file.url}
              loader={contentfulLoader}
              width={origWidth}
              height={origHeight}
            />
          </InternalLink>
        );
      }

      return (
        <ExternalLink className="relative" href={url}>
          <NextImageWrapper
            imageClassName="relative my-0 h-auto max-w-full rounded-lg"
            customPlaceholderClassName="rounded-lg"
            alt={image.fields.title || 'blogpost image'}
            src={image.fields.file.url}
            loader={contentfulLoader}
            width={origWidth}
            height={origHeight}
          />
        </ExternalLink>
      );
    },
    [BLOCKS.EMBEDDED_ASSET]: (node: Node) => {
      const { description, file } = node.data.target.fields;
      const mimeType = file.contentType;
      const mimeGroup = mimeType.split('/')[0];
      if (mimeGroup === 'image') {
        const { width: origWidth, height: origHeight } = file.details.image;
        const contentfulLoader = ({ src, width, quality }: ImageLoaderProps): string =>
          getContentfulLoader(src, width, origWidth, origHeight, {
            quality,
          });
        return (
          <figure className="relative flex items-center justify-center">
            <NextImageWrapper
              imageClassName="relative my-0 h-auto max-w-full rounded-lg"
              customPlaceholderClassName="rounded-lg"
              alt={description || 'blogpost image'}
              src={file.url}
              loader={contentfulLoader}
              width={origWidth}
              height={origHeight}
            />
            <figcaption className="text-midnight mt-2 text-center text-sm italic">
              {description}
            </figcaption>
          </figure>
        );
      }
      if (ACCEPTED_VIDEO_MIME_TYPES.includes(mimeType)) {
        return (
          <video width="100%" controls>
            <source src={file.url} type={mimeType} />
          </video>
        );
      }
      reportMykissError(
        new Error(`Unsupported mimetype for Contentful asset: ${mimeType}`),
        'error',
      );
      return null;
    },
    [INLINES.HYPERLINK]: (node: Node, children: React.ReactNode) => (
      <ExternalLink target="_blank" href={`${node.data.uri}`}>
        {children}
      </ExternalLink>
    ),
  },
  renderMark: {
    [MARKS.CODE]: (content: React.ReactNode) => {
      if (isBrowser() && typeof content === 'string' && content.startsWith('<iframe')) {
        // TODO fix hydration error FIB-42278
        return (
          <p
            className="my-4 flex justify-center"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(content, {
                allowedTags: ['iframe'],
                allowedAttributes: {
                  iframe: ['src'],
                },
                allowedIframeHostnames: ['www.youtube.com'],
              }),
            }}
          />
        );
      }
      return <code>content</code>;
    },
  },
});
